import {keys, platform} from 'substance'

class DWKeyboardManager {

    constructor({substanceAddKeyboardShortcut}) {
        this.npwKeybindings = {}
        this.rawKeybindings = []
        this.commandNameToShortcutMap = {}
        this.substanceAddKeyboardShortcut = substanceAddKeyboardShortcut
    }

    /**
     * Returns the shortcut key combo for the specified command name.
     * @param commandName The registered name for a command
     * @return {string} Empty string if not found.
     */
    getShortcutForCommand(commandName) {
        return this.commandNameToShortcutMap[commandName] || ''
    }

    /**
     * @param {KeyboardEvent} event
     * @returns {string|boolean}
     */
    getCommandForKeyDownEvent(event) {
        const key = this.generateKey(event)
        const hook = this.npwKeybindings[key]

        return hook ? hook.command : false
    }

    /**
     * Adds a keyboard shortcut
     *
     * @param {string|object} combo - String representing a keyboard shortcut, eg. 'ctrl+s', 'alt+i', 'ctrl+alt+delete'
     * @param {object} spec
     * @param {boolean} globalCombo
     * @param {string} description
     */
    addKeyboardShortcut(combo, spec, globalCombo, description) {
        if (typeof combo === 'string') {
            this.rawKeybindings.push({combo, spec, globalCombo, description})
            if (spec.command) {
                this.commandNameToShortcutMap[spec.command] = combo
            }

            if (globalCombo === true) {
                this.npwKeybindings[this.parseCombo(combo)] = spec
            } else {
                this.substanceAddKeyboardShortcut(combo, spec)
            }
        } else if (typeof combo === 'object') {
            this.addComplexKeyboardShortcut(combo, spec, globalCombo, description)
        }
    }

    /**
     * Add keyboard shortcut and connect to a command
     *
     * combo = {
     *   "override": {
     *     "default": "ctrl+alt+h",
     *     "mac":"cmd+alt+h"
     *   },
     *   "standard": {
     *     "default": "ctrl+shift+h",
     *     "mac": "cmd+shift+h"
     *   }
     * }
     *
     * or
     *
     * combo = {
     *   "override": "cmd+alt+h",
     *   "standard": "cmd+alt+1"
     * }
     *
     * `hardcodedShortcuts` is what the plugin defines the shortcuts to be.
     * `configuredShortcuts` is what was configured in the writer config file.
     *
     * @param {string|object} combo Key combination, ie "cmd+alt+h". Or, {object} combo.
     * @param {object} spec Command to run ie "{command: Mycommand}"
     * @param {boolean} globalCombo Optional, if true key combination works outside the editor surface
     * @param {string} [description=]
     */
    addComplexKeyboardShortcut(combo, spec, globalCombo, description = '') {

        const {override, standard} = combo

        combo = undefined

        if (standard && standard.default) {
            combo = standard.default
        } else if (standard) {
            combo = standard
        }

        if (platform.isMac) {
            if (override && override.mac) {
                combo = override.mac
            } else if (override) {
                combo = override
            } else if (standard && standard.mac) {
                combo = standard.mac
            }
        } else {
            if (override && override.default) {
                combo = override.default
            } else if (override) {
                combo = override
            }
        }

        if (combo !== undefined) {
            this.addKeyboardShortcut(combo, spec, globalCombo, description)
        }
    }

    generateKey(event) {
        let frags = []
        if (event.altKey) {
            if (event.code === 'AltRight') {
                frags.push('ALTGR')
            }
            else {
                frags.push('ALT')
            }
        }

        if (event.ctrlKey) frags.push('CTRL')
        if (event.metaKey) frags.push('META')
        if (event.shiftKey) frags.push('SHIFT')
        frags.push(event.keyCode)

        return frags.join('+')
    }

    parseCombo(combo) {
        let frags = combo.split('+')
        let data = {
            keyCode: -1
        }
        for (var i = 0; i < frags.length; i++) {
            let frag = frags[i].toUpperCase()
            switch (frag) {
                case 'ALT': {
                    data.altKey = true
                    break
                }

                case 'ALTGR': {
                    data.altKey = true
                    data.code = 'AltRight'
                    break
                }

                case 'CMD': {
                    data.metaKey = true
                    break
                }

                case 'CTRL': {
                    data.ctrlKey = true
                    break
                }

                case 'SHIFT': {
                    data.shiftKey = true
                    break
                }

                default:
                    if (frag.length === 1) {
                        data.keyCode = frag.charCodeAt(0)
                    }
                    else if (keys.hasOwnProperty(frag)) {
                        data.keyCode = keys[frag]
                    }
                    else {
                        throw new Error('Unsupported keyboard command: ' + combo)
                    }
            }
        }

        return this.generateKey(data)
    }
}

export {DWKeyboardManager}
